// src/components/CartDrawer.js
import React from 'react';
import { Link } from 'react-router-dom';
import { X, Minus, Plus, ShoppingBag, Trash2, ArrowRight } from 'lucide-react';
import { useApp } from '../context/AppContext';

export default function CartDrawer() {
  const { cart, cartOpen, setCartOpen, cartTotal, removeFromCart, updateCartQuantity } = useApp();

  if (!cartOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
        onClick={() => setCartOpen(false)}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-md bg-white shadow-2xl flex flex-col animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-rose-100">
          <div className="flex items-center gap-2">
            <ShoppingBag size={20} className="text-rose-500" />
            <h2 className="font-display font-semibold text-lg text-gray-900">Your Cart</h2>
            {cart.length > 0 && (
              <span className="bg-rose-100 text-rose-600 text-xs font-bold px-2 py-0.5 rounded-full">
                {cart.length}
              </span>
            )}
          </div>
          <button
            onClick={() => setCartOpen(false)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-20 h-20 bg-rose-50 rounded-full flex items-center justify-center mb-4">
                <ShoppingBag size={32} className="text-rose-300" />
              </div>
              <h3 className="font-display font-semibold text-gray-700 mb-2">Your cart is empty</h3>
              <p className="text-gray-400 text-sm font-body mb-6">Add some love to your cart 💕</p>
              <button
                onClick={() => setCartOpen(false)}
                className="bg-rose-500 text-white px-6 py-3 rounded-2xl font-medium hover:bg-rose-600 transition-colors"
              >
                Browse Gifts
              </button>
            </div>
          ) : (
            cart.map((item) => (
              <div key={item.id} className="flex gap-4 bg-rose-50/50 rounded-2xl p-3">
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-20 h-20 object-cover rounded-xl flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <h4 className="font-body font-medium text-gray-900 text-sm line-clamp-2">{item.name}</h4>
                  <div className="flex items-center justify-between mt-2">
                    <span className="font-display font-bold text-rose-600 text-sm">
                      ₹{(item.price * item.quantity).toLocaleString('en-IN')}
                    </span>
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  {/* Quantity */}
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      onClick={() => updateCartQuantity(item.id, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                      className="w-6 h-6 bg-white rounded-full flex items-center justify-center border border-rose-200 hover:border-rose-400 transition-colors disabled:opacity-40"
                    >
                      <Minus size={12} />
                    </button>
                    <span className="text-sm font-body font-medium text-gray-700 w-6 text-center">
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => updateCartQuantity(item.id, item.quantity + 1)}
                      className="w-6 h-6 bg-white rounded-full flex items-center justify-center border border-rose-200 hover:border-rose-400 transition-colors"
                    >
                      <Plus size={12} />
                    </button>
                    <span className="text-xs text-gray-400 font-body ml-1">
                      × ₹{item.price.toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {cart.length > 0 && (
          <div className="p-6 border-t border-rose-100 bg-white">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-500 font-body text-sm">Subtotal</span>
              <span className="font-display font-bold text-xl text-gray-900">
                ₹{cartTotal.toLocaleString('en-IN')}
              </span>
            </div>
            <p className="text-xs text-gray-400 font-body mb-4">Shipping & taxes calculated at checkout</p>
            <Link
              to="/checkout"
              onClick={() => setCartOpen(false)}
              className="w-full flex items-center justify-center gap-2 bg-rose-500 hover:bg-rose-600 text-white py-4 rounded-2xl font-medium transition-all shadow-rose hover:shadow-rose-lg"
            >
              Proceed to Checkout <ArrowRight size={18} />
            </Link>
            <Link
              to="/products"
              onClick={() => setCartOpen(false)}
              className="w-full flex items-center justify-center mt-3 text-rose-500 hover:text-rose-600 text-sm font-body transition-colors"
            >
              Continue Shopping
            </Link>
          </div>
        )}
      </div>
    </>
  );
}
